import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const SESSION_STATUS_COLORS = {
  PENDING_COMPONENTS: 'bg-amber-100 text-amber-800',
  COMPONENTS_READY: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-purple-100 text-purple-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-stone-200 text-stone-700',
};

export default function ComboV2PlanDetail() {
  const navigate = useNavigate();
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, [planId]);

  const load = async () => {
    try {
      setLoading(true);
      const tk = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/api/combo-v2/plans/${planId}`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setPlan(res.data);
    } catch (e) {
      toast.error('Failed to load plan');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] p-6">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] p-6">
        <p className="text-stone-500">Plan not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5] p-6" data-testid="plan-detail-page">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/wms/combo-v2/plans')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">{plan.plan_name}</h1>
            <p className="text-xs text-stone-500 font-mono">{plan.plan_id}</p>
          </div>
          <Badge className="ml-auto">{plan.status}</Badge>
        </div>

        {plan.issues?.length > 0 && (
          <Card className="p-4 mb-4 bg-amber-50 border-amber-300">
            <div className="font-semibold mb-2">Plan Issues</div>
            <ul className="text-sm list-disc pl-5">
              {plan.issues.map((iss, i) => (
                <li key={i}>{iss}</li>
              ))}
            </ul>
          </Card>
        )}

        <div className="grid grid-cols-3 gap-4 mb-4">
          <Card className="p-4">
            <div className="text-xs text-stone-500">Sessions</div>
            <div className="text-2xl font-bold">{plan.sessions?.length || 0}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-stone-500">Pick Slips</div>
            <div className="text-2xl font-bold">{plan.slips?.length || 0}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs text-stone-500">Total Qty</div>
            <div className="text-2xl font-bold">
              {(plan.sessions || []).reduce((a, s) => a + (s.target_quantity || 0), 0)}
            </div>
          </Card>
        </div>

        <Card className="mb-4">
          <div className="p-4 border-b font-semibold">Sessions</div>
          <table className="w-full text-sm" data-testid="sessions-table">
            <thead className="bg-stone-50">
              <tr>
                <th className="text-left p-3">Session</th>
                <th className="text-left p-3">Combo</th>
                <th className="text-left p-3">Design / Shade / Size</th>
                <th className="text-right p-3">Target</th>
                <th className="text-right p-3">Assembled</th>
                <th className="text-center p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {(plan.sessions || []).map(s => (
                <tr key={s.session_id} className="border-t" data-testid={`session-row-${s.session_id}`}>
                  <td className="p-3 font-mono text-xs">{s.session_id}</td>
                  <td className="p-3">{s.combo_barcode}</td>
                  <td className="p-3">
                    {s.design_name} / {s.shade} / {s.size}
                  </td>
                  <td className="p-3 text-right font-medium">{s.target_quantity}</td>
                  <td className="p-3 text-right">{s.assembled_quantity || 0}</td>
                  <td className="p-3 text-center">
                    <Badge className={SESSION_STATUS_COLORS[s.live_status] || 'bg-stone-100'}>
                      {s.live_status || '—'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card>
          <div className="p-4 border-b font-semibold">Pick Slips</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="slips-table">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left p-3">Slip No</th>
                  <th className="text-left p-3">Component</th>
                  <th className="text-left p-3">Design / Shade / Size</th>
                  <th className="text-left p-3">Description</th>
                  <th className="text-right p-3">Bundles</th>
                  <th className="text-right p-3">Pcs</th>
                </tr>
              </thead>
              <tbody>
                {(plan.slips || []).map(s => {
                  const dss = [s.design_name, s.shade, s.size]
                    .filter(Boolean)
                    .join(' / ');
                  return (
                    <tr key={s.slip_no} className="border-t" data-testid={`slip-row-${s.slip_no}`}>
                      <td className="p-3 font-mono text-xs">{s.slip_no}</td>
                      <td className="p-3 font-mono text-xs">{s.component_barcode}</td>
                      <td className="p-3" data-testid={`slip-dss-${s.slip_no}`}>
                        {dss || <span className="text-stone-400">—</span>}
                      </td>
                      <td className="p-3 text-stone-600" data-testid={`slip-desc-${s.slip_no}`}>
                        {s.description || <span className="text-stone-400">—</span>}
                      </td>
                      <td className="p-3 text-right">{s.total_bundles}</td>
                      <td className="p-3 text-right font-medium">{s.pick_qty}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
